import React, { useState, useEffect, useMemo, useRef } from "react";
import WeatherWidget from "../widgets/WeatherWidget";
import QuickLinksPill from "../widgets/QuickLinksPill";
import StatisticsWidget from "../widgets/StatisticsWidget";
import { useAuth } from "../../contexts/AuthContext";
import oneSpaceApi from "../../lib/onespace-api";
import { Loader2, CheckCircle } from "lucide-react";
import { Button } from "../ui/button";
import QuickActions from "../widgets/QuickActions";
import GoalTrackingWidget from "../widgets/GoalTrackingWidget";

// API QuickLink interface
interface ApiQuickLink {
  id?: string;
  name: string;
  value: string;
  type: 'url' | 'application';
  icon: string | null;
  description?: string | null;
  is_visible?: boolean;
  is_favorite?: boolean;
  team_id?: string | number | null;
  user_id?: string | number | null;
  order?: number;
  created_at?: string;
  updated_at?: string;
}

interface QuickLinksResponse {
  all: ApiQuickLink[];
  grouped: {
    team_id: string | number | null;
    team_name: string;
    links: ApiQuickLink[];
  }[];
  favorites: ApiQuickLink[];
}

// Frontend display interface
interface DisplayQuickLink {
  title: string;
  url: string;
  icon: string;
}

// Widget types that can be displayed
type WidgetType = 'sales' | 'tracker' | 'notifications' | 'goals' | 'pomodoro' | 'notes' | 'calendar' | 'weather' | 'analytics' | 'wiki';

interface Widget {
  id: string;
  type: WidgetType;
  title: string;
  content: React.ReactNode;
  timeSlots?: string[]; // Time slots when this widget should be active
  colspan?: number; // Number of columns this widget should span (1, 2, or 3)
}

interface WeatherData {
  temperature: number;
  condition: string;
  location: string;
  icon?: string;
}

// Task interface from API
interface ApiTask {
  id: string;
  title: string;
  description?: string | null;
  completed: boolean;
  completed_at?: string | null;
  order: number;
  user_id?: string;
  team_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Pomodoro session interface
interface PomodoroSession {
  tasks: { id?: string; text: string; completed: boolean }[]; // Tasks now have optional id from API
  currentTaskIndex: number; // Index of current task
  duration: number; // in minutes
  startTime: number; // timestamp
  isActive: boolean;
}

// Note interface
interface Note {
  id?: string;
  content: string;
  created_at?: string;
}

// Grid configuration interface
interface GridConfig {
  mobile_columns: number; // Number of columns on mobile (e.g., 2 for 2x2)
  desktop_columns: number; // Number of columns on desktop (e.g., 4 for 4x4)
  max_rows?: number; // Optional max rows limit
}

interface CentralSectionProps {
  settings?: any;
  weatherData?: WeatherData;
  isWeatherLoading?: boolean;
  userName?: string;
  quickLinks?: QuickLinksResponse;
  layout?: 'centered' | 'left-aligned';
  gridConfig?: GridConfig; // Grid configuration from settings
  centralWidgets?: any[]; // Central widgets data from API
}

// Cookie helper functions
const setCookie = (name: string, value: string, days: number = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const CentralSection: React.FC<CentralSectionProps> = ({ 
  settings,
  weatherData,
  isWeatherLoading,
  userName = "Daniel",
  quickLinks,
  layout = 'left-aligned',
  gridConfig,
  centralWidgets
}) => {
  const { isAuthenticated } = useAuth();
  const [time, setTime] = useState(new Date());
  const [showPomodoroSetup, setShowPomodoroSetup] = useState(false);
  const [pomodoroSession, setPomodoroSession] = useState<PomodoroSession | null>(() => {
    // Load active session from cookie if exists
    const savedSession = getCookie('pomodoroSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        // Check if session is still valid (not older than 24 hours)
        if (session.startTime && (Date.now() - session.startTime) < 24 * 60 * 60 * 1000) {
          // Handle old session format (with 'goal' instead of 'tasks')
          if (session.goal && !session.tasks) {
            return {
              tasks: [{ text: session.goal, completed: false }],
              currentTaskIndex: 0,
              duration: session.duration,
              startTime: session.startTime,
              isActive: session.isActive
            };
          }
          // Handle old tasks format (array of strings)
          if (session.tasks && Array.isArray(session.tasks) && session.tasks.length > 0) {
            if (typeof session.tasks[0] === 'string') {
              return {
                ...session,
                tasks: session.tasks.map((task: string) => ({ text: task, completed: false }))
              };
            }
            // New format with task objects
            return session;
          }
        }
      } catch (e) {
        console.error('Failed to parse pomodoro session:', e);
      }
    }
    return null;
  });
  const [pomodoroTimeRemaining, setPomodoroTimeRemaining] = useState<number>(0);
  
  // Pomodoro setup state
  const [pomodoroTasks, setPomodoroTasks] = useState<{ id?: string; text: string; completed: boolean }[]>([]);
  const [currentTaskInput, setCurrentTaskInput] = useState('');
  const [pomodoroDuration, setPomodoroDuration] = useState(25);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  
  // Notes state
  const [showNotesOverlay, setShowNotesOverlay] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [activeNote, setActiveNote] = useState<Note | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSaveStatus, setNoteSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [noteError, setNoteError] = useState<string | null>(null);
  
  // File transfer state
  const [showFileTransferOverlay, setShowFileTransferOverlay] = useState(false);
  const [fileTransferMode, setFileTransferMode] = useState<'choose' | 'send' | 'receive'>('choose');
  
  // Send mode state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [downloadLink, setDownloadLink] = useState<string>('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Send options
  const [downloadLimit, setDownloadLimit] = useState<number>(1);
  const [linkPassword, setLinkPassword] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [pincode, setPincode] = useState<string>('');
  
  // Receive mode state
  const [uploadLink, setUploadLink] = useState<string>('');
  const [isGeneratingUploadLink, setIsGeneratingUploadLink] = useState(false);
  
  // Receive options
  const [uploadDescription, setUploadDescription] = useState<string>('');
  const [receiveDeadline, setReceiveDeadline] = useState<string>('');
  
  // Refs for auto-save
  const noteSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const noteSaveIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Default grid configuration if none provided
  const defaultGridConfig: GridConfig = {
    mobile_columns: 2,
    desktop_columns: 3,
    max_rows: 3
  };
  
  const currentGridConfig = gridConfig || defaultGridConfig;
  
  // Generate dynamic grid classes based on configuration
  const getGridClasses = () => {
    const mobileClass = `grid-cols-${currentGridConfig.mobile_columns}`;
    const desktopClass = `lg:grid-cols-${currentGridConfig.desktop_columns}`;
    return `grid ${mobileClass} ${desktopClass} gap-4`;
  };
  
  // Generate colspan classes based on current grid configuration
  const getColspanClasses = (colspan?: number) => {
    if (!colspan || colspan === 1) return 'col-span-1';
    
    // Ensure colspan doesn't exceed grid columns
    const mobileSpan = Math.min(colspan, currentGridConfig.mobile_columns);
    const desktopSpan = Math.min(colspan, currentGridConfig.desktop_columns);
    
    if (mobileSpan === desktopSpan) {
      return `col-span-${mobileSpan}`;
    } else {
      return `col-span-${mobileSpan} lg:col-span-${desktopSpan}`;
    }
  };
  
  // Fetch tasks from API when Pomodoro setup opens
  const fetchTasks = async () => {
    if (!isAuthenticated || !showPomodoroSetup) return;
    
    setIsLoadingTasks(true);
    try {
      const response = await oneSpaceApi.get('/api/tasks', {
        params: { completed: false }
      });
      
      const tasks: ApiTask[] = response.data;
      
      // Convert API tasks to frontend format
      const formattedTasks = tasks.map(task => ({
        id: task.id,
        text: task.title,
        completed: task.completed
      }));
      
      if (formattedTasks.length > 0) {
        setPomodoroTasks(formattedTasks);
      } else {
        setPomodoroTasks([{ text: '', completed: false }]);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      // Fallback to empty task list
      setPomodoroTasks([{ text: '', completed: false }]);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  // Load tasks when Pomodoro setup opens
  useEffect(() => {
    if (showPomodoroSetup && isAuthenticated) {
      fetchTasks();
    }
  }, [showPomodoroSetup, isAuthenticated]);
  
  // Transform API quicklinks to display format
  const displayLinks: DisplayQuickLink[] = useMemo(() => {
    if (quickLinks && quickLinks.all && quickLinks.all.length > 0) {
      console.log("Using provided API quicklinks:", quickLinks.all);
      return quickLinks.all.map(link => ({
        title: link.name,
        url: link.value,
        icon: link.icon || 'link'
      }));
    }
    
    return [
      {
        title: "Google",
        url: "https://www.google.com",
        icon: "google"
      },
      {
        title: "Gmail",
        url: "https://mail.google.com",
        icon: "gmail"
      },
      {
        title: "YouTube",
        url: "https://www.youtube.com",
        icon: "youtube"
      }
    ];
  }, [quickLinks]);

  // Get favorites from quicklinks
  const favoriteLinks: DisplayQuickLink[] = useMemo(() => {
    if (quickLinks && quickLinks.favorites && quickLinks.favorites.length > 0) {
      return quickLinks.favorites.map(link => ({
        title: link.name,
        url: link.value,
        icon: link.icon || 'link'
      }));
    }
    // Fallback to first 3 display links as favorites if no API favorites
    return displayLinks.slice(0, 3);
  }, [quickLinks, displayLinks]);

  // Get grouped links with fallback data
  const groupedLinks = useMemo(() => {
    if (quickLinks && quickLinks.grouped && quickLinks.grouped.length > 0) {
      console.log("Using provided API grouped links:", quickLinks.grouped);
      return quickLinks.grouped;
    }
    
    // Fallback grouped data for testing
    return [
      {
        team_id: null,
        team_name: "Persoonlijk",
        links: [
          {
            id: "1",
            name: "GitHub",
            value: "https://github.com",
            type: 'url' as const,
            icon: "github",
            is_favorite: false,
            team_id: null,
            user_id: "1"
          },
          {
            id: "2", 
            name: "Stack Overflow",
            value: "https://stackoverflow.com",
            type: 'url' as const,
            icon: "link",
            is_favorite: true,
            team_id: null,
            user_id: "1"
          }
        ]
      },
      {
        team_id: 1,
        team_name: "Development Team",
        links: [
          {
            id: "3",
            name: "Jira",
            value: "https://company.atlassian.net",
            type: 'url' as const,
            icon: "link",
            is_favorite: false,
            team_id: 1,
            user_id: "1"
          },
          {
            id: "4",
            name: "Confluence",
            value: "https://company.atlassian.net/wiki",
            type: 'url' as const,
            icon: "link",
            is_favorite: false,
            team_id: 1,
            user_id: "1"
          },
          {
            id: "5",
            name: "Slack",
            value: "https://company.slack.com",
            type: 'url' as const,
            icon: "link",
            is_favorite: true,
            team_id: 1,
            user_id: "1"
          }
        ]
      }
    ];
  }, [quickLinks]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => clearInterval(timer);
  }, []);

  // Pomodoro timer effect
  useEffect(() => {
    if (pomodoroSession && pomodoroSession.isActive) {
      const interval = setInterval(() => {
        const elapsed = Date.now() - pomodoroSession.startTime;
        const remaining = (pomodoroSession.duration * 60 * 1000) - elapsed;
        
        if (remaining <= 0) {
          // Timer completed
          setPomodoroTimeRemaining(0);
          
          // Play notification sound if available
          const audio = new Audio('/notification.mp3');
          audio.play().catch(e => console.log('Audio play failed:', e));
          
          // Show browser notification
          if (Notification.permission === 'granted' && pomodoroSession.tasks && pomodoroSession.tasks[pomodoroSession.currentTaskIndex]) {
            new Notification('Pomodoro Voltooid!', {
              body: `Je hebt je taak afgerond: ${pomodoroSession.tasks[pomodoroSession.currentTaskIndex].text}`,
              icon: '/favicon.ico'
            });
          }
          
          // Clear the session
          setPomodoroSession(null);
          setPomodoroTasks([{ text: '', completed: false }]);
          setPomodoroDuration(25);
        } else {
          setPomodoroTimeRemaining(Math.floor(remaining / 1000));
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [pomodoroSession]);

  // Save pomodoro session to cookie whenever it changes
  useEffect(() => {
    if (pomodoroSession) {
      setCookie('pomodoroSession', JSON.stringify(pomodoroSession));
    } else {
      // Clear cookie if session is null
      setCookie('pomodoroSession', '', -1);
    }
  }, [pomodoroSession]);

  // Pomodoro functions
  const startPomodoroSession = () => {
    const validTasks = pomodoroTasks.filter(task => task.text.trim());
    if (validTasks.length === 0) {
      alert('Voeg minimaal één taak toe voor je focus sessie');
      return;
    }

    const newSession: PomodoroSession = {
      tasks: validTasks,
      currentTaskIndex: 0,
      duration: pomodoroDuration,
      startTime: Date.now(),
      isActive: true
    };

    setPomodoroSession(newSession);
    setShowPomodoroSetup(false);
    setPomodoroTimeRemaining(pomodoroDuration * 60);
    setCurrentTaskInput('');
    setPomodoroTasks([{ text: '', completed: false }]);
  };

  const pausePomodoroSession = () => {
    if (pomodoroSession) {
      setPomodoroSession({
        ...pomodoroSession,
        isActive: false
      });
    }
  };

  const resumePomodoroSession = () => {
    if (pomodoroSession) {
      setPomodoroSession({
        ...pomodoroSession,
        isActive: true
      });
    }
  };

  const stopPomodoroSession = () => {
    setPomodoroSession(null);
    setPomodoroTimeRemaining(0);
    setPomodoroTasks([{ text: '', completed: false }]);
    setPomodoroDuration(25);
  };

  // Format time for Pomodoro display
  const formatPomodoroTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Task management functions
  const addTask = async () => {
    if (currentTaskInput.trim()) {
      setIsSavingTask(true);
      try {
        if (isAuthenticated) {
          // Save to API
          const response = await oneSpaceApi.post('/api/tasks', {
            title: currentTaskInput.trim()
          });
          
          const newTask: ApiTask = response.data;
          
          // Add to local state with API ID
          const filteredTasks = pomodoroTasks.filter(t => t.text.trim());
          setPomodoroTasks([...filteredTasks, { 
            id: newTask.id,
            text: newTask.title, 
            completed: newTask.completed 
          }]);
        } else {
          // Just add locally if not authenticated
          const filteredTasks = pomodoroTasks.filter(t => t.text.trim());
          setPomodoroTasks([...filteredTasks, { text: currentTaskInput.trim(), completed: false }]);
        }
        setCurrentTaskInput('');
      } catch (err) {
        console.error('Error adding task:', err);
        alert('Kan taak niet toevoegen. Probeer het opnieuw.');
      } finally {
        setIsSavingTask(false);
      }
    }
  };

  const removeTask = async (displayIndex: number) => {
    const filteredTasks = pomodoroTasks.filter(t => t.text.trim());
    const taskToRemove = filteredTasks[displayIndex];
    
    if (taskToRemove?.id && isAuthenticated) {
      try {
        await oneSpaceApi.delete(`/api/tasks/${taskToRemove.id}`);
      } catch (err) {
        console.error('Error deleting task:', err);
      }
    }
    
    const newTasks = filteredTasks.filter((_, i) => i !== displayIndex);
    if (newTasks.length === 0) {
      setPomodoroTasks([{ text: '', completed: false }]);
    } else {
      setPomodoroTasks(newTasks);
    }
  };

  const moveTaskUp = async (displayIndex: number) => {
    if (displayIndex > 0) {
      const filteredTasks = pomodoroTasks.filter(t => t.text.trim());
      const newTasks = [...filteredTasks];
      [newTasks[displayIndex - 1], newTasks[displayIndex]] = [newTasks[displayIndex], newTasks[displayIndex - 1]];
      setPomodoroTasks(newTasks);
      
      // Update order in API if authenticated
      if (isAuthenticated) {
        await updateTaskOrder(newTasks);
      }
    }
  };

  const moveTaskDown = async (displayIndex: number) => {
    const filteredTasks = pomodoroTasks.filter(t => t.text.trim());
    if (displayIndex < filteredTasks.length - 1) {
      const newTasks = [...filteredTasks];
      [newTasks[displayIndex], newTasks[displayIndex + 1]] = [newTasks[displayIndex + 1], newTasks[displayIndex]];
      setPomodoroTasks(newTasks);
      
      // Update order in API if authenticated
      if (isAuthenticated) {
        await updateTaskOrder(newTasks);
      }
    }
  };

  // Update task order in API
  const updateTaskOrder = async (tasks: { id?: string; text: string; completed: boolean }[]) => {
    const tasksWithIds = tasks.filter(t => t.id);
    if (tasksWithIds.length === 0) return;
    
    try {
      await oneSpaceApi.post('/api/tasks/order', {
        tasks: tasksWithIds.map((task, index) => ({
          id: task.id,
          order: index
        }))
      });
    } catch (err) {
      console.error('Error updating task order:', err);
    }
  };

  // Toggle task completion during setup
  const toggleTaskCompletion = async (displayIndex: number) => {
    const filteredTasks = pomodoroTasks.filter(t => t.text.trim());
    const newTasks = [...filteredTasks];
    const task = newTasks[displayIndex];
    
    task.completed = !task.completed;
    setPomodoroTasks(newTasks);
    
    // Update in API if task has ID
    if (task.id && isAuthenticated) {
      try {
        await oneSpaceApi.post(`/api/tasks/${task.id}/toggle`, {
          completed: task.completed
        });
      } catch (err) {
        console.error('Error toggling task:', err);
        // Revert on error
        task.completed = !task.completed;
        setPomodoroTasks([...newTasks]);
      }
    }
  };

  // Toggle task completion in active session
  const toggleSessionTaskCompletion = async (taskIndex: number) => {
    if (!pomodoroSession) return;
    
    const task = pomodoroSession.tasks[taskIndex];
    if (!task) return;
    
    const updatedSession = {
      ...pomodoroSession,
      tasks: pomodoroSession.tasks.map((t, index) => 
        index === taskIndex 
          ? { ...t, completed: !t.completed }
          : t
      )
    };
    
    // If the current task was completed, automatically move to the next incomplete task
    if (taskIndex === pomodoroSession.currentTaskIndex && !task.completed) {
      // Find the next incomplete task
      let nextIncompleteIndex = -1;
      for (let i = taskIndex + 1; i < updatedSession.tasks.length; i++) {
        if (!updatedSession.tasks[i].completed) {
          nextIncompleteIndex = i;
          break;
        }
      }
      
      // If no incomplete tasks after current, check from beginning
      if (nextIncompleteIndex === -1) {
        for (let i = 0; i < taskIndex; i++) {
          if (!updatedSession.tasks[i].completed) {
            nextIncompleteIndex = i;
            break;
          }
        }
      }
      
      // Update current task index if we found an incomplete task
      if (nextIncompleteIndex !== -1) {
        updatedSession.currentTaskIndex = nextIncompleteIndex;
      }
    }
    
    setPomodoroSession(updatedSession);
    
    // Update in API if task has ID
    if (task.id && isAuthenticated) {
      try {
        await oneSpaceApi.post(`/api/tasks/${task.id}/toggle`, {
          completed: !task.completed
        });
      } catch (err) {
        console.error('Error toggling task:', err);
        // Revert on error
        setPomodoroSession(pomodoroSession);
      }
    }
  };

  // Notes API functions
  const fetchNotes = async () => {
    if (!isAuthenticated || !showNotesOverlay) return;
    
    setIsLoadingNotes(true);
    setNoteError(null);

    try {
      const response = await oneSpaceApi.get('/api/notes');
      let notesArray: Note[] = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          notesArray = response.data;
        } else if (Array.isArray(response.data.data)) {
          notesArray = response.data.data;
        } else if (response.data.data && typeof response.data.data === 'object') {
          notesArray = [response.data.data];
        } else if (typeof response.data === 'object') {
          notesArray = [response.data];
        }
      }

      setNotes(notesArray);
      
      // Select the most recent note
      if (notesArray.length > 0) {
        const sortedNotes = [...notesArray].sort((a, b) => {
          if (!a.created_at && !b.created_at) return 0;
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        const latestNote = sortedNotes[0];
        setActiveNote(latestNote);
        setNoteContent(latestNote.content);
      } else {
        // Create a new note if none exist
        createNewNote();
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
      setNoteError("Kan notities niet laden.");
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const createNewNote = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await oneSpaceApi.post('/api/notes', { content: "" });
      let newNote: Note;
      
      if (response.data) {
        if (response.data.data && typeof response.data.data === 'object') {
          newNote = response.data.data;
        } else if (typeof response.data === 'object') {
          newNote = response.data;
        } else {
          newNote = { content: "", id: Date.now().toString() };
        }
        
        setNotes([newNote, ...notes]);
        setActiveNote(newNote);
        setNoteContent(newNote.content);
      }
    } catch (err) {
      console.error("Error creating note:", err);
      setNoteError("Kan notitie niet maken.");
    }
  };

  const updateNote = async (noteId: string, content: string) => {
    if (!isAuthenticated || !noteId) return;
    
    setIsSavingNote(true);
    setNoteSaveStatus("saving");
    
    try {
      await oneSpaceApi.put(`/api/notes/${noteId}`, {
        content: content
      });
      
      // Update note in state
      setNotes(notes.map(note => 
        note.id === noteId 
          ? { ...note, content: content }
          : note
      ));
      
      // Show saved indicator
      setNoteSaveStatus("saved");
      
      // Reset save indicator after 2 seconds
      if (noteSaveIndicatorTimeoutRef.current) {
        clearTimeout(noteSaveIndicatorTimeoutRef.current);
      }
      
      noteSaveIndicatorTimeoutRef.current = setTimeout(() => {
        setNoteSaveStatus("idle");
      }, 2000);
      
    } catch (err) {
      console.error("Error updating note:", err);
      setNoteError("Kan notitie niet opslaan.");
    } finally {
      setIsSavingNote(false);
    }
  };

  // Load notes when overlay opens
  useEffect(() => {
    if (showNotesOverlay) {
      fetchNotes();
    }
  }, [showNotesOverlay, isAuthenticated]);

  // Auto-save notes (debounced)
  useEffect(() => {
    if (!activeNote?.id || !showNotesOverlay) return;
    
    // Clear previous timeout
    if (noteSaveTimeoutRef.current) {
      clearTimeout(noteSaveTimeoutRef.current);
    }
    
    // Set new timeout for saving
    noteSaveTimeoutRef.current = setTimeout(() => {
      updateNote(activeNote.id!, noteContent);
    }, 500); // 500ms debounce
    
    return () => {
      if (noteSaveTimeoutRef.current) {
        clearTimeout(noteSaveTimeoutRef.current);
      }
    };
  }, [noteContent, activeNote?.id]);

  // Handle escape key to close notes overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (showNotesOverlay || showPomodoroSetup || showFileTransferOverlay)) {
        setShowNotesOverlay(false);
        setShowPomodoroSetup(false);
        setShowFileTransferOverlay(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showNotesOverlay, showPomodoroSetup, showFileTransferOverlay]);

  const formattedTime = useMemo(() => {
    return time.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }, [time]);

  const greeting = useMemo(() => {
    const hour = time.getHours();
    if (hour < 12) return "Goedemorgen";
    if (hour < 18) return "Goedemiddag";
    if (hour < 21) return "Goedenavond";
    if (hour >= 23 || hour < 7) return "Goedenacht";
    return "Goedendag";
  }, [time]);

  // Get quarterly goals from API central widgets
  const quarterlyGoals = useMemo(() => {
    if (!centralWidgets || !Array.isArray(centralWidgets)) return [];
    
    const goalsWidget = centralWidgets.find(widget => widget.widget_type === 'goal_tracking');
    return goalsWidget?.data?.goals || [];
  }, [centralWidgets]);

  // Generate widgets from API data
  const availableWidgets: Widget[] = useMemo(() => {
    if (!centralWidgets || !Array.isArray(centralWidgets)) return [];
    
    return centralWidgets.map(widgetData => {
      // Create the widget content based on widget_type
      let content: React.ReactNode = null;
      
      switch (widgetData.widget_type) {
        case 'statistics':
          content = (
            <StatisticsWidget
              id={widgetData.id}
              title={widgetData.title}
              statistic_type={widgetData.config.statistic_type}
              period={widgetData.config.period}
              currency={widgetData.config.currency}
              current_value={widgetData.data.current_value}
              current_count={widgetData.data.current_count}
              target={widgetData.config.target}
              target_count={widgetData.config.target_count}
              trend={widgetData.data.trend}
              trend_direction={widgetData.data.trend_direction}
            />
          );
          break;
          
        case 'goal_tracking':
          content = (
            <GoalTrackingWidget
              id={widgetData.id}
              title={widgetData.title}
              goals={widgetData.data.goals || []}
              max_goals={widgetData.config.max_goals || 5}
              compact_mode={widgetData.config.compact_mode || false}
            />
          );
          break;
          
        default:
          content = (
            <div className="p-4 text-center text-gray-500">
              Onbekend widget type: {widgetData.widget_type}
            </div>
          );
      }
      
      return {
        id: widgetData.id,
        type: widgetData.type as WidgetType,
        title: widgetData.title,
        colspan: widgetData.colspan || 1,
        content: content
      };
    });
  }, [centralWidgets]);

  // Get current widgets - show all available widgets (API will control which ones to display)
  const currentWidgets = useMemo(() => {
    return availableWidgets;
  }, [availableWidgets]);

  // File transfer functions
  const generateDownloadLink = async () => {
    if (!selectedFiles.length) return;
    
    // Generate pincode automatically
    const generatedPincode = generatePincode();
    console.log('Generated pincode:', generatedPincode); // Debug log
    setPincode(generatedPincode);
    
    setIsGeneratingLink(true);
    try {
      if (isAuthenticated) {
        // Generate download link via API
        const response = await oneSpaceApi.post('/api/file-transfer/generate-download-link', {
          filenames: selectedFiles.map(file => file.name),
          filesizes: selectedFiles.map(file => file.size),
          filetypes: selectedFiles.map(file => file.type),
          download_limit: downloadLimit,
          password: linkPassword || null,
          expires_at: expiryDate || null,
          pincode: generatedPincode
        });
        
        if (response.data?.download_link) {
          setDownloadLink(response.data.download_link);
        }
      } else {
        // Generate demo link
        const demoLink = `https://files.onespace.cloud/d/${Math.random().toString(36).substring(2, 12)}`;
        setDownloadLink(demoLink);
      }
    } catch (err) {
      console.error('Error generating download link:', err);
      alert('Kon geen download link genereren. Probeer het opnieuw.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const generateUploadLink = async () => {
    setIsGeneratingUploadLink(true);
    try {
      if (isAuthenticated) {
        // Generate upload link via API
        const response = await oneSpaceApi.post('/api/file-transfer/generate-upload-link', {
          description: uploadDescription || null,
          deadline: receiveDeadline || null
        });
        
        if (response.data?.upload_link) {
          setUploadLink(response.data.upload_link);
        }
      } else {
        // Generate demo link
        const demoLink = `https://files.onespace.cloud/u/${Math.random().toString(36).substring(2, 12)}`;
        setUploadLink(demoLink);
      }
    } catch (err) {
      console.error('Error generating upload link:', err);
      alert('Kon geen upload link genereren. Probeer het opnieuw.');
    } finally {
      setIsGeneratingUploadLink(false);
    }
  };

  const uploadFilesWithLink = async () => {
    if (!selectedFiles.length || !downloadLink) return;
    
    setIsUploading(true);
    try {
      if (isAuthenticated) {
        const formData = new FormData();
        selectedFiles.forEach((file, index) => {
          formData.append('files', file);
          formData.append('download_limit', downloadLimit.toString());
          formData.append('password', linkPassword);
          formData.append('expires_at', expiryDate);
        });
        
        await oneSpaceApi.post('/api/file-transfer/upload-with-link', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        alert(`Bestanden succesvol geüpload!`);
      } else {
        // Demo mode - simulate upload
        setTimeout(() => {
          alert(`Demo: Bestanden "${selectedFiles.map(file => file.name).join(', ')}" geüpload!`);
        }, 1000);
      }
    } catch (err) {
      console.error('Error uploading files:', err);
      alert('Kon bestanden niet uploaden. Probeer het opnieuw.');
    } finally {
      setIsUploading(false);
    }
  };

  const resetFileTransfer = () => {
    setFileTransferMode('choose');
    setSelectedFiles([]);
    setDownloadLink('');
    setUploadLink('');
    setDownloadLimit(1);
    setLinkPassword('');
    setExpiryDate('');
    setPincode('');
    setUploadDescription('');
    setReceiveDeadline('');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      
      // Check each file size (5MB limit per file)
      const oversizedFiles = fileArray.filter(file => file.size > 5 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        alert(`De volgende bestanden zijn te groot (max 5MB per bestand): ${oversizedFiles.map(f => f.name).join(', ')}`);
        return;
      }
      
      // Check total size (25MB total limit)
      const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > 25 * 1024 * 1024) {
        alert('Totale bestandsgrootte mag niet meer dan 25MB zijn.');
        return;
      }
      
      // Check file count (max 10 files)
      if (fileArray.length > 10) {
        alert('Je kunt maximaal 10 bestanden tegelijk uploaden.');
        return;
      }
      
      setSelectedFiles(fileArray);
      setDownloadLink('');
    }
  };

  const removeFile = (indexToRemove: number) => {
    setSelectedFiles(selectedFiles.filter((_, index) => index !== indexToRemove));
  };

  const getTotalFileSize = () => {
    return selectedFiles.reduce((sum, file) => sum + file.size, 0);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Link gekopieerd naar clipboard!');
    } catch (err) {
      console.error('Could not copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link gekopieerd naar clipboard!');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get minimum date (today) for date inputs
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Generate 6-digit pincode
  const generatePincode = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Main dashboard layout
  return (
    <div>
        {/* Main Content - 50/50 Split */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8 min-h-[600px]">
          {/* Left Column - Time and Greeting (50%) */}
          <div className="flex flex-col justify-center">
            {pomodoroSession ? (
              // Focus Mode - Simplified view during Pomodoro
              <div className="text-center">
                <h2 className="text-2xl font-medium text-gray-600 dark:text-gray-400 mb-8">
                  Focus Sessie Actief
                </h2>
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-lg p-8 max-w-md mx-auto">
                  <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">Huidige taak:</p>
                  <div className="flex items-center gap-3 mb-6">
                    <input
                      type="checkbox"
                      checked={pomodoroSession.tasks[pomodoroSession.currentTaskIndex]?.completed || false}
                      onChange={() => toggleSessionTaskCompletion(pomodoroSession.currentTaskIndex)}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary w-5 h-5"
                    />
                    <h3 className={`text-2xl font-semibold text-gray-800 dark:text-white ${
                      pomodoroSession.tasks[pomodoroSession.currentTaskIndex]?.completed ? 'line-through opacity-60' : ''
                    }`}>
                      {pomodoroSession.tasks && pomodoroSession.tasks[pomodoroSession.currentTaskIndex] 
                        ? pomodoroSession.tasks[pomodoroSession.currentTaskIndex].text
                        : 'Geen taak geselecteerd'}
                    </h3>
                  </div>
                  
                  {/* Show remaining tasks if any */}
                  {pomodoroSession.tasks && pomodoroSession.tasks.length > 1 && pomodoroSession.currentTaskIndex < pomodoroSession.tasks.length && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Volgende taken ({pomodoroSession.tasks.length - pomodoroSession.currentTaskIndex - 1}):
                      </p>
                      <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                        {pomodoroSession.tasks.slice(pomodoroSession.currentTaskIndex + 1).slice(0, 3).map((task, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => toggleSessionTaskCompletion(pomodoroSession.currentTaskIndex + 1 + index)}
                              className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary w-4 h-4"
                            />
                            <span className={`truncate ${task.completed ? 'line-through opacity-60' : ''}`}>
                              {task.text}
                            </span>
                          </li>
                        ))}
                        {pomodoroSession.tasks.length - pomodoroSession.currentTaskIndex - 1 > 3 && (
                          <li className="text-gray-400 dark:text-gray-500">
                            ... en {pomodoroSession.tasks.length - pomodoroSession.currentTaskIndex - 4} meer
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Normal view - Time and greeting
              <div className="mb-8">
                <WeatherWidget 
                    weatherData={weatherData || undefined} 
                    isLoading={isWeatherLoading} 
                  />
                
                <h1 className="text-[7rem] font-semibold tracking-[0.01em] leading-none text-theme">
          {formattedTime}
        </h1>
                <h2 className="text-3xl font-normal text-theme opacity-90 text-shadow pb-4">
                  {greeting} {userName},
        </h2>
                
                <QuickActions 
                  quickLinks={quickLinks}
                  onShowNotesOverlay={() => setShowNotesOverlay(true)}
                  onShowPomodoroSetup={() => setShowPomodoroSetup(true)}
                  onShowFileTransfer={() => setShowFileTransferOverlay(true)}
                />
              </div>
            )}
      </div>
      
          {/* Right Column - Widgets Grid or Active Timer (50%) */}
          {pomodoroSession ? (
            // Active Pomodoro Timer
            <div className="flex items-center justify-center h-full">
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-xl p-12 text-center max-w-md w-full">
                <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-4">Focus Sessie</h3>
                
                {/* Timer Display */}
                <div className="text-7xl font-bold mb-6 text-primary">
                  {formatPomodoroTime(pomodoroTimeRemaining)}
                </div>
                
                {/* Goal Display */}
                <div className="mb-8">
                  <p className="text-sm text-gray-500 dark:text-gray-500 mb-2">Huidige taak:</p>
                  <div className="flex items-center justify-center gap-3">
                    <input
                      type="checkbox"
                      checked={pomodoroSession.tasks && pomodoroSession.tasks[pomodoroSession.currentTaskIndex]?.completed || false}
                      onChange={() => toggleSessionTaskCompletion(pomodoroSession.currentTaskIndex)}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary w-5 h-5"
                    />
                    <p className={`text-xl font-medium text-gray-800 dark:text-gray-200 ${
                      pomodoroSession.tasks && pomodoroSession.tasks[pomodoroSession.currentTaskIndex]?.completed ? 'line-through opacity-60' : ''
                    }`}>
                      {pomodoroSession.tasks && pomodoroSession.tasks[pomodoroSession.currentTaskIndex] 
                        ? pomodoroSession.tasks[pomodoroSession.currentTaskIndex].text
                        : 'Geen taak geselecteerd'}
                    </p>
                  </div>
                  {pomodoroSession.tasks && pomodoroSession.tasks.length > 1 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      Taak {pomodoroSession.currentTaskIndex + 1} van {pomodoroSession.tasks.length}
                    </p>
                  )}
                </div>
                
                {/* Control Buttons */}
                <div className="flex gap-3 justify-center">
                  {pomodoroSession.isActive ? (
            <button
                      onClick={pausePomodoroSession}
                      className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                    >
                      Pauzeer
            </button>
                  ) : (
            <button
                      onClick={resumePomodoroSession}
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                    >
                      Hervat
            </button>
                  )}
            <button
                    onClick={stopPomodoroSession}
                    className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                  >
                    Stop
            </button>
          </div>

                {/* Progress Bar */}
                <div className="mt-8">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-1000"
                      style={{ 
                        width: `${((pomodoroSession.duration * 60 - pomodoroTimeRemaining) / (pomodoroSession.duration * 60)) * 100}%` 
                      }}
                    ></div>
        </div>
                </div>
              </div>
            </div>
          ) : (
            // Widgets Container with potential Notes Overlay
            <div className="relative h-full">

              {/* Quote Section */}
              {settings?.quote && (
                <div className="mb-6 text-center bg-white/90 p-4 shadow dark:bg-gray-800/90 backdrop-blur-sm rounded-xl">
                  <p className="text-lg text-theme font-bold">
                    {settings.quote}
                  </p>
                </div>
              )}
              {/* Widgets Grid */}
              <div className={getGridClasses()}>
                {currentWidgets.map((widget, index) => (
                  <div
                    key={widget.id}
                    className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 min-h-[180px] overflow-hidden ${getColspanClasses(widget.colspan)}`}
                  >
                    {widget.content}
                  </div>
                ))}
              </div>

              {/* Notes Overlay - Only over widgets */}
              {showNotesOverlay && (
                <div className="absolute inset-0 z-20">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-sm rounded-2xl"></div>
                  <div className="absolute inset-0 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Snelle Notitie</h3>
                      <div className="flex items-center gap-3">
                        {/* Save Status */}
                        <div className="flex items-center">
                          {noteSaveStatus === "saving" && (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              <span className="text-xs text-muted-foreground ml-1">Opslaan...</span>
                            </>
                          )}
                          {noteSaveStatus === "saved" && (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-xs text-green-500 ml-1">Opgeslagen</span>
                            </>
                          )}
                        </div>
                        
                        {/* Close Button */}
                        <button
                          onClick={() => setShowNotesOverlay(false)}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Sluiten (Esc)"
                        >
                          <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-4 overflow-hidden">
                      {isLoadingNotes ? (
                        <div className="flex items-center justify-center h-full">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                      ) : !isAuthenticated ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Log in om notities te maken
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Je moet ingelogd zijn om notities op te slaan.
                            </p>
                          </div>
              </div>
            ) : (
                        <div className="h-full flex flex-col">
                          {/* Error message */}
                          {noteError && (
                            <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                              <p className="text-xs text-red-600 dark:text-red-400">{noteError}</p>
              </div>
                          )}
                          
                          {/* Note editor */}
                          <textarea
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                            placeholder="Begin met typen..."
                            className="flex-1 w-full p-3  resize-none bg-transparent outline-none focus-visible:ring-0 focus-visible:ring-offset-0 border-0 shadow-none text-gray-800 dark:text-white placeholder-gray-400 text-sm"
                            autoFocus
                          />
                          
                          {/* Footer info */}
                          <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <p>Tip: Druk op Esc om te sluiten</p>
                            {activeNote?.created_at && (
                              <p className="text-right">Bijgewerkt: {new Date(activeNote.created_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</p>
                            )}
                          </div>
          </div>
        )}
                    </div>
                  </div>
          </div>
        )}

              {/* File Transfer Overlay - Only over widgets */}
              {showFileTransferOverlay && (
                <div className="absolute inset-0 z-20">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-sm rounded-2xl"></div>
                  <div className="absolute inset-0 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {fileTransferMode === 'choose' && 'Bestand Overdragen'}
                        {fileTransferMode === 'send' && 'Bestand Verzenden'}
                        {fileTransferMode === 'receive' && 'Bestand Ontvangen'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowFileTransferOverlay(false);
                          resetFileTransfer();
                        }}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Sluiten (Esc)"
                      >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-4 overflow-y-auto">
                      {fileTransferMode === 'choose' && (
                        /* Choice Mode - Send or Receive */
                        <div className="space-y-6">
                          <div className="text-center mb-8">
                            <h4 className="text-xl font-medium text-gray-800 dark:text-white mb-2">
                              Wat wil je doen?
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Kies of je een bestand wilt verzenden of ontvangen
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Send Option */}
                            <div 
                              onClick={() => setFileTransferMode('send')}
                              className="cursor-pointer p-6 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
                            >
                              <div className="text-center">
                                <svg className="w-12 h-12 mx-auto mb-4 text-blue-500 group-hover:text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                                  Bestand Verzenden
                                </h5>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Upload een bestand en genereer een download link om te delen
                                </p>
                              </div>
                            </div>

                            {/* Receive Option */}
                            <div 
                              onClick={() => setFileTransferMode('receive')}
                              className="cursor-pointer p-6 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
                            >
                              <div className="text-center">
                                <svg className="w-12 h-12 mx-auto mb-4 text-green-500 group-hover:text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l2 2 4-4" />
                                </svg>
                                <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                                  Bestand Ontvangen
                                </h5>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Genereer een upload link om bestanden van anderen te ontvangen
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Feature Overview */}
                          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                            <h6 className="text-sm font-medium text-gray-800 dark:text-white mb-2">Functies:</h6>
                            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                              <li>• Maximaal 5MB per bestand, 25MB totaal</li>
                              <li>• Tot 10 bestanden tegelijk</li>
                              <li>• Automatische 6-cijferige pincode</li>
                              <li>• Optionele wachtwoordbeveiliging</li>
                              <li>• Download limieten</li>
                              <li>• Automatische vervaldatum</li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {fileTransferMode === 'send' && (
                        /* Send Mode - Upload file and generate download link */
                        <div className="space-y-6">
                          {/* Back button */}
                          <button
                            onClick={() => setFileTransferMode('choose')}
                            className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Terug
                          </button>

                          {/* File Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Selecteer bestanden om te verzenden
                            </label>
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary transition-colors">
                              <input
                                type="file"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="sendFileInput"
                                accept="*/*"
                                multiple
                              />
                              <label htmlFor="sendFileInput" className="cursor-pointer flex flex-col items-center">
                                <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  Klik om bestanden te selecteren
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  Maximaal 5MB per bestand • 10 bestanden • 25MB totaal
                                </span>
                              </label>
                            </div>
                            
                            {/* Selected Files List */}
                            {selectedFiles.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-gray-800 dark:text-white">
                                    {selectedFiles.length} bestand{selectedFiles.length !== 1 ? 'en' : ''} geselecteerd
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Totaal: {formatFileSize(getTotalFileSize())}
                                  </p>
                                </div>
                                
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                  {selectedFiles.map((file, index) => (
                                    <div key={`${file.name}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                          {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          {formatFileSize(file.size)} • {file.type || 'Onbekend type'}
                                        </p>
                                      </div>
                                      <button
                                        onClick={() => removeFile(index)}
                                        className="ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                        title="Verwijder bestand"
                                      >
                                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Clear all button */}
                                <button
                                  onClick={() => setSelectedFiles([])}
                                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                                >
                                  Alle bestanden verwijderen
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Send Options */}
                          {selectedFiles.length > 0 && (
                            <div className="space-y-4">
                              <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300">Opties</h6>
                              
                              {/* Download Limit */}
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                  Aantal keren downloaden
                                </label>
                                <select
                                  value={downloadLimit}
                                  onChange={(e) => setDownloadLimit(Number(e.target.value))}
                                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-white text-sm"
                                >
                                  <option value={1}>1 keer</option>
                                  <option value={5}>5 keer</option>
                                  <option value={10}>10 keer</option>
                                  <option value={25}>25 keer</option>
                                  <option value={0}>Onbeperkt</option>
                                </select>
                              </div>

                              {/* Password */}
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                  Wachtwoord (optioneel)
                                </label>
                                <input
                                  type="password"
                                  value={linkPassword}
                                  onChange={(e) => setLinkPassword(e.target.value)}
                                  placeholder="Voer wachtwoord in..."
                                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-white text-sm"
                                />
                              </div>

                              {/* Expiry Date */}
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                  Beschikbaar tot (optioneel)
                                </label>
                                <input
                                  type="date"
                                  value={expiryDate}
                                  onChange={(e) => setExpiryDate(e.target.value)}
                                  min={getMinDate()}
                                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-white text-sm"
                                />
                              </div>
                            </div>
                          )}

                          {/* Generate Link */}
                          {selectedFiles.length > 0 && !downloadLink && (
                            <Button
                              onClick={generateDownloadLink}
                              disabled={isGeneratingLink}
                              className="w-full"
                            >
                              {isGeneratingLink ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  Genereren...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  Genereer Download Link
                                </>
                              )}
                            </Button>
                          )}

                          {/* Download Link Result */}
                          {downloadLink && (
                            <div className="space-y-3">
                              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <h6 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                                  Download link gegenereerd!
                                </h6>
                                
                                {/* Download Link */}
                                <div className="mb-4">
                                  <label className="block text-xs text-green-700 dark:text-green-400 mb-1">
                                    Download Link:
                                  </label>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={downloadLink}
                                      readOnly
                                      className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-lg text-gray-800 dark:text-white text-sm font-mono"
                                    />
                                    <Button
                                      onClick={() => copyToClipboard(downloadLink)}
                                      variant="outline"
                                      size="sm"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </Button>
                                  </div>
                                </div>

                                {/* Pincode */}
                                {pincode && (
                                  <div className="mb-4">
                                    <label className="block text-xs text-green-700 dark:text-green-400 mb-1">
                                      Pincode (verplicht voor download):
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={pincode}
                                        readOnly
                                        className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-lg text-gray-800 dark:text-white text-lg font-mono text-center tracking-widest"
                                      />
                                      <Button
                                        onClick={() => copyToClipboard(pincode)}
                                        variant="outline"
                                        size="sm"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Debug info - remove later */}
                                <div className="text-xs text-gray-500 mb-2">
                                  Debug: Pincode state = "{pincode}" (length: {pincode.length})
                                </div>
                                
                                <p className="text-xs text-green-700 dark:text-green-400">
                                  Deel zowel de link als de pincode met de ontvanger om de bestanden te downloaden.
                                </p>
                              </div>

                              {/* Reset button */}
                              <Button
                                onClick={resetFileTransfer}
                                variant="outline"
                                className="w-full"
                              >
                                Nieuwe Bestanden Verzenden
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {fileTransferMode === 'receive' && (
                        /* Receive Mode - Generate upload link for others */
                        <div className="space-y-6">
                          {/* Back button */}
                          <button
                            onClick={() => setFileTransferMode('choose')}
                            className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Terug
                          </button>

                          <div className="text-center mb-6">
                            <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-2">
                              Bestand Ontvangen
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Genereer een upload link om bestanden van anderen te ontvangen
                            </p>
                          </div>

                          {/* Receive Options */}
                          <div className="space-y-4">
                            <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300">Instellingen</h6>
                            
                            {/* Upload Description */}
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Toelichting bij uploaden (optioneel)
                              </label>
                              <textarea
                                value={uploadDescription}
                                onChange={(e) => setUploadDescription(e.target.value)}
                                placeholder="Bijv: Upload hier je CV en motivatiebrief..."
                                rows={3}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-white text-sm resize-none"
                              />
                            </div>

                            {/* Deadline */}
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Aanleveren voor datum (optioneel)
                              </label>
                              <input
                                type="date"
                                value={receiveDeadline}
                                onChange={(e) => setReceiveDeadline(e.target.value)}
                                min={getMinDate()}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-white text-sm"
                              />
                            </div>
                          </div>

                          {/* Generate Upload Link */}
                          {!uploadLink && (
                            <Button
                              onClick={generateUploadLink}
                              disabled={isGeneratingUploadLink}
                              className="w-full"
                            >
                              {isGeneratingUploadLink ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  Genereren...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  Genereer Upload Link
                                </>
                              )}
                            </Button>
                          )}

                          {/* Upload Link Result */}
                          {uploadLink && (
                            <div className="space-y-3">
                              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <h6 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                                  Upload link gegenereerd!
                                </h6>
                                <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                                  Stuur onderstaande link naar degene die het bestand moet aanleveren:
                                </p>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={uploadLink}
                                    readOnly
                                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-lg text-gray-800 dark:text-white text-sm font-mono"
                                  />
                                  <Button
                                    onClick={() => copyToClipboard(uploadLink)}
                                    variant="outline"
                                    size="sm"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </Button>
                                </div>
                                
                                {/* Summary */}
                                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                                  <p className="text-xs text-blue-700 dark:text-blue-400">
                                    <strong>Instellingen:</strong> 5MB per bestand
                                    {receiveDeadline && ` • Deadline: ${new Date(receiveDeadline).toLocaleDateString('nl-NL')}`}
                                  </p>
                                </div>
                              </div>

                              {/* Reset button */}
                              <Button
                                onClick={resetFileTransfer}
                                variant="outline"
                                className="w-full"
                              >
                                Nieuwe Upload Link Genereren
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        Tip: Druk op Esc om te sluiten • Max 10 bestanden, 5MB per bestand, 25MB totaal
                      </p>
                    </div>
                  </div>
                </div>
              )}
                    </div>
          )}
        </div>


      {/* Pomodoro Setup Popup */}
      {showPomodoroSetup && (
        <div className="fixed top-0  left-0 w-full h-full bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Start Focus Sessie</h2>
            
            {/* Task List */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Wat wil je bereiken in deze sessie?
              </label>
              
              {isLoadingTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Taken laden...</span>
              </div>
              ) : (
                        <>
                  {/* Existing tasks */}
                  <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                    {pomodoroTasks.filter(task => task.text.trim()).map((task, index) => (
                      <div key={task.id || index} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={task.completed}
                          onChange={() => toggleTaskCompletion(index)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-6">{index + 1}.</span>
                        <span className={`flex-1 text-gray-800 dark:text-white ${task.completed ? 'line-through opacity-60' : ''}`}>
                          {task.text}
                                </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => moveTaskUp(index)}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Omhoog"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                                </svg>
                          </button>
                          <button
                            onClick={() => moveTaskDown(index)}
                            disabled={index === pomodoroTasks.filter(t => t.text.trim()).length - 1}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Omlaag"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => removeTask(index)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-600 dark:text-red-400"
                            title="Verwijderen"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                    </div>
                  </div>
                ))}
                    {pomodoroTasks.filter(t => t.text.trim()).length === 0 && !isLoadingTasks && (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                        {isAuthenticated ? 'Geen actieve taken gevonden. Voeg een nieuwe taak toe!' : 'Voeg je eerste taak toe!'}
              </div>
            )}
              </div>
                  
                  {/* Add new task */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={currentTaskInput}
                      onChange={(e) => setCurrentTaskInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !isSavingTask && addTask()}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Nieuwe taak toevoegen..."
                      disabled={isSavingTask}
                    />
                    <button
                      onClick={addTask}
                      disabled={isSavingTask || !currentTaskInput.trim()}
                      className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isSavingTask ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Bezig...
                        </>
                      ) : (
                        'Toevoegen'
                      )}
                    </button>
      </div>
          </>
        )}
      </div>
            
            {/* Duration Input */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hoe lang wil je focussen?
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="5"
                  max="60"
                  step="5"
                  value={pomodoroDuration}
                  onChange={(e) => setPomodoroDuration(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-lg font-medium text-gray-800 dark:text-white w-16 text-right">
                  {pomodoroDuration} min
                </span>
              </div>
              {/* Quick duration buttons */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setPomodoroDuration(15)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    pomodoroDuration === 15 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  15 min
                </button>
                <button
                  onClick={() => setPomodoroDuration(25)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    pomodoroDuration === 25 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  25 min
                </button>
                <button
                  onClick={() => setPomodoroDuration(45)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    pomodoroDuration === 45 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  45 min
                </button>
                <button
                  onClick={() => setPomodoroDuration(60)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    pomodoroDuration === 60 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  60 min
                </button>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPomodoroSetup(false);
                  setPomodoroTasks([]);
                  setPomodoroDuration(25);
                }}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={startPomodoroSession}
                disabled={isLoadingTasks}
                className="flex-1 px-6 py-3 bg-primary hover:bg-primary/80 text-primary-foreground rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Focus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentralSection; 